import { useCallback, useContext, useEffect, useState } from 'react'
import {
  BillStatus,
  BillType,
  OutstandingBillsPayload,
  useFetchOutstandingBillsLazyQuery,
  useFetchPublicOutstandingBillsLazyQuery
} from '../../generated'
import { FINAL_BILL_STATUSES, GRAPHQL_QUERY_POLICY, PRE_BILL_STATUSES } from '../constants'
import { UseFetchOutstandingBillsResult } from '../interfaceTypes'
import { AuthContext } from '../context/AuthContext'

interface UseFetchOutstandingBills {
  caseId: string | undefined
  billId: number | undefined
  billStatus: BillStatus | undefined
  billType: BillType | undefined
  clientId: string | undefined
  token: string | undefined
  isPrebillView?: boolean
}

const useFetchOutstandingBills = ({
  caseId,
  billId,
  billStatus,
  billType,
  clientId,
  token,
  isPrebillView
}: UseFetchOutstandingBills): UseFetchOutstandingBillsResult => {
  const [outstandingBills, setOutstandingBills] = useState<OutstandingBillsPayload['outstandingBills']>([])
  const [previousBalance, setPreviousBalance] = useState<number>(0)
  const { currentUser } = useContext(AuthContext)

  const [
    fetchOutstandingBills,
    { loading: outstandingBillsLoading, error: outstandingBillsError, refetch: refetchOutstandingBills }
  ] = useFetchOutstandingBillsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onCompleted(data) {
      if (data?.fetchOutstandingBills) {
        const { response, outstandingBills, previousBalance: previousBalanceData } = data.fetchOutstandingBills
        if (response?.status === 200) {
          setOutstandingBills(outstandingBills)
          setPreviousBalance(previousBalanceData || 0)
        }
      }
    }
  })

  const [
    fetchPublicOutstandingBills,
    {
      loading: publicOutstandingBillsLoading,
      error: publicOutstandingBillsError,
      refetch: refetchPublicOutstandingBills
    }
  ] = useFetchPublicOutstandingBillsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onCompleted(data) {
      if (data?.fetchPublicOutstandingBills) {
        const { response, outstandingBills, previousBalance: previousBalanceData } = data.fetchPublicOutstandingBills
        if (response?.status === 200) {
          setOutstandingBills(outstandingBills)
          setPreviousBalance(previousBalanceData || 0)
        }
      }
    }
  })

  const performFetchOutstandingBills = useCallback(
    (isRefetch: boolean) => {
      const finalBillStatuses: BillStatus[] = FINAL_BILL_STATUSES.filter(
        status => status !== BillStatus.Paid && status !== BillStatus.PrePaid
      )
      if (
        caseId &&
        billStatus &&
        billType &&
        billType === BillType.Bill &&
        (PRE_BILL_STATUSES.includes(billStatus) || finalBillStatuses.includes(billStatus))
      ) {
        const isFinalBill = !isPrebillView && FINAL_BILL_STATUSES.includes(billStatus)
        const excludeBillId = PRE_BILL_STATUSES.includes(billStatus) ? billId : undefined

        if (currentUser?.id) {
          if (isRefetch) {
            refetchOutstandingBills({
              fetchOutstandingBillsInput: { caseId, excludeBillId, ...(isFinalBill ? { clientId } : {}) }
            })
          } else {
            fetchOutstandingBills({
              variables: {
                fetchOutstandingBillsInput: { caseId, excludeBillId, ...(isFinalBill ? { clientId } : {}) }
              }
            })
          }
        } else {
          if (isRefetch) {
            refetchPublicOutstandingBills({
              fetchOutstandingBillsInput: { caseId, ...(isFinalBill ? { clientId } : {}), token }
            })
          } else {
            fetchPublicOutstandingBills({
              variables: {
                fetchOutstandingBillsInput: { caseId, ...(isFinalBill ? { clientId } : {}), token }
              }
            })
          }
        }
      }
    },
    [
      caseId,
      billStatus,
      billType,
      isPrebillView,
      currentUser?.id,
      fetchOutstandingBills,
      refetchOutstandingBills,
      clientId,
      billId,
      fetchPublicOutstandingBills,
      refetchPublicOutstandingBills,
      token
    ]
  )

  const refetchOutstandingBillsCallback = useCallback(() => {
    performFetchOutstandingBills(true)
  }, [performFetchOutstandingBills])

  useEffect(() => {
    performFetchOutstandingBills(false)

    return () => {
      setOutstandingBills([])
      setPreviousBalance(0)
    }
  }, [performFetchOutstandingBills])

  return {
    outstandingBills,
    previousBalance,
    loading: outstandingBillsLoading || publicOutstandingBillsLoading,
    error: outstandingBillsError || publicOutstandingBillsError,
    refetch: refetchOutstandingBillsCallback
  }
}

export default useFetchOutstandingBills
